import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "sp-anime:favorites";

export type FavoriteKind = "episode" | "character";

export interface FavoriteItem {
  kind: FavoriteKind;
  id: string;
}

function readFavorites(): FavoriteItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item): item is FavoriteItem =>
        !!item &&
        (item.kind === "episode" || item.kind === "character") &&
        typeof item.id === "string"
    );
  } catch {
    return [];
  }
}

function writeFavorites(items: FavoriteItem[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new Event("sp-favorites-change"));
  } catch {
    /* ignore quota errors */
  }
}

export function useFavorites() {
  const [favorites, setFavorites] = useState<FavoriteItem[]>(readFavorites);

  useEffect(() => {
    const sync = () => setFavorites(readFavorites());
    window.addEventListener("sp-favorites-change", sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener("sp-favorites-change", sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const isFavorite = useCallback(
    (kind: FavoriteKind, id: string) =>
      favorites.some((item) => item.kind === kind && item.id === id),
    [favorites]
  );

  const toggleFavorite = useCallback((kind: FavoriteKind, id: string) => {
    const current = readFavorites();
    const exists = current.some((item) => item.kind === kind && item.id === id);
    const next = exists
      ? current.filter((item) => !(item.kind === kind && item.id === id))
      : [...current, { kind, id }];
    writeFavorites(next);
    setFavorites(next);
    return !exists;
  }, []);

  return { favorites, isFavorite, toggleFavorite };
}